import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Database, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { downloadApiFile, formatUploadError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function DatabaseBackup() {
  const [file, setFile] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const input = useRef();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const backup = async () => {
    setExporting(true);
    try {
      await downloadApiFile("/admin/backup", `plazakreasi-backup-${new Date().toISOString().slice(0, 10)}.json`);
      toast.success("Backup berhasil diunduh");
    } catch { toast.error("Gagal mengunduh backup"); }
    finally { setExporting(false); }
  };
  const restore = async () => {
    setRestoring(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post("/admin/restore", form, { timeout: 180000 });
      toast.success("Database dipulihkan. Silakan login dengan akun pada backup.");
      await logout(); navigate("/login");
    } catch (error) { toast.error(formatUploadError(error)); }
    finally { setRestoring(false); setFile(null); }
  };
  return <section className="border border-border bg-card p-4 sm:p-6 mb-8">
    <h2 className="font-heading font-bold text-sm uppercase tracking-wider flex items-center gap-2"><Database size={16} className="text-primary" /> Backup dan Restore Database</h2>
    <p className="text-sm text-muted-foreground my-3">Backup berisi data meeting, akun, departemen, dan referensi file. Isi file Recording dan Manual Minutes perlu dicadangkan terpisah di storage.</p>
    <div className="flex gap-3 flex-wrap">
      <Button variant="outline" disabled={exporting || restoring} onClick={backup} data-testid="backup-db-btn"><Download size={16} className="mr-2" />{exporting ? "Mengunduh..." : "Unduh Backup"}</Button>
      <Button variant="outline" disabled={exporting || restoring} onClick={() => input.current.click()} data-testid="restore-db-btn"><Upload size={16} className="mr-2" /> Restore dari JSON</Button>
      <input ref={input} type="file" accept=".json,application/json" className="hidden" onChange={(e) => {
        const selected = e.target.files?.[0];
        e.target.value = "";
        if (selected?.size > 50 * 1024 * 1024) return toast.error("Backup maksimal 50 MB");
        if (selected) setFile(selected);
      }} />
    </div>
    <AlertDialog open={Boolean(file)} onOpenChange={(open) => { if (!open && !restoring) setFile(null); }}>
      <AlertDialogContent className="rounded-none" onEscapeKeyDown={(event) => { if (restoring) event.preventDefault(); }}>
        <AlertDialogHeader><AlertDialogTitle>Pulihkan database dari backup?</AlertDialogTitle>
          <AlertDialogDescription>File {file?.name} akan mengganti seluruh data meeting, pengguna, dan departemen. Akun serta password mengikuti isi backup. Cadangkan data saat ini sebelum melanjutkan. Selama pemulihan, akses aplikasi akan dijeda. File di storage tidak ikut dipindahkan atau dihapus.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel disabled={restoring}>Batal</AlertDialogCancel>
          <Button onClick={restore} disabled={restoring}>{restoring ? "Memulihkan..." : "Pulihkan Data"}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </section>;
}
